import { Box } from "@mui/material";
import { Chart as ChartJS, registerables } from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { useEffect, useMemo, useState } from "react";
import { Bar } from "react-chartjs-2";

import { ISummary } from "./types";

ChartJS.register(...registerables, ChartDataLabels);

interface IProps {
  beneficiaries: ISummary[];
}

interface IOrganizationData {
  organizationName: string;
  totalCount: number;
}

export function GraphTotal({ beneficiaries }: IProps) {
  const [chartData, setChartData] = useState<any>({
    labels: [],
    datasets: [],
  });

  // Agrupa os beneficiários por organização e conta o total
  const organizationData: IOrganizationData[] = useMemo(() => {
    const organizationMap = new Map<string, number>();

    beneficiaries.forEach((beneficiary) => {
      const currentCount =
        organizationMap.get(beneficiary.organizationName) || 0;
      organizationMap.set(beneficiary.organizationName, currentCount + 1);
    });

    return Array.from(organizationMap.entries()).map(
      ([organizationName, totalCount]) => ({
        organizationName,
        totalCount,
      })
    );
  }, [beneficiaries]);

  useEffect(() => {
    const labels = organizationData.map((item) => item.organizationName);
    const values = organizationData.map((item) => item.totalCount);

    setChartData({
      labels: labels,
      datasets: [
        {
          label: "População total",
          data: values,
          backgroundColor: "#3300FF",
          borderColor: "#3300FF",
          borderWidth: 1,
          borderRadius: { bottomRight: 5, topRight: 5 },
          barThickness: 20,
          maxBarThickness: 20,
        },
      ],
    });
  }, [organizationData]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          justifyContent: "center",
          paddingTop: "0.25rem",
          paddingBottom: "1rem",
          flexShrink: 0,
        }}
      >
        <span
          style={{
            fontFamily: "Sora",
            fontSize: "1.25rem",
            fontWeight: 600,
            lineHeight: "2rem",
            color: "rgba(15, 15, 30, 0.6)",
          }}
        >
          Vidas - Empregados e Dependentes
        </span>
      </Box>
      <Box
        sx={{
          padding: "1rem",
          flexGrow: 1,
          minHeight: 0,
          position: "relative",
        }}
      >
        <Bar
          data={chartData}
          options={{
            indexAxis: "y" as const,
            // Adiciona layout com padding para garantir espaço para os labels
            layout: {
              padding: {
                right: 50, // Adiciona espaço à direita para os valores
                left: 10,
                top: 10,
                bottom: 10,
              },
            },
            plugins: {
              datalabels: {
                align: "end",
                anchor: "end",
                color: "#000000",
                font: {
                  weight: "bold",
                  size: 14,
                },
                formatter: (value: number) => value.toLocaleString(),
                // Adiciona offset para garantir que o texto não seja cortado
                offset: 4,
                // Adiciona clipping false para garantir que os labels sejam renderizados
                clip: false,
              },
              legend: {
                display: true,
                position: "top",
                align: "end",
                labels: {
                  usePointStyle: true,
                  color: "#1E1B39",
                  font: {
                    weight: "bold",
                    size: 14,
                    lineHeight: 18,
                  },
                },
              },
            },
            elements: {
              bar: {
                borderWidth: 2,
              },
            },
            scales: {
              x: {
                grid: {
                  display: true,
                  drawBorder: false,
                  borderDash: [10, 10],
                  color: "rgba(0, 0, 0, 0.1)",
                },
                ticks: {
                  color: "#1E1B39",
                },
                // Adiciona uma margem de segurança no eixo X
                afterFit: (scaleInstance) => {
                  scaleInstance.width += 20;
                },
              },
              y: {
                grid: {
                  display: false,
                },
                ticks: {
                  color: "#1E1B39",
                  font: {
                    size: 14,
                  },
                },
              },
            },
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
              intersect: false,
              mode: "index" as const,
            },
            animation: {
              duration: 0,
            },
            onHover: undefined,
            onResize: undefined,
          }}
        />
      </Box>
    </>
  );
}
